import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import ApiService from '../services/apiService';

const FluctuationWarning: React.FC = () => {
  const [showWarning, setShowWarning] = useState<boolean>(false);
  const [weightData, setWeightData] = useState<{
    currentWeight: number;
    expectedWeight: number;
    deviation: number;
    severity: 'none' | 'slight' | 'significant';
    inExperience: boolean;
  }>({
    currentWeight: 0,
    expectedWeight: 0,
    deviation: 0,
    severity: 'none',
    inExperience: false,
  });

  const location = useLocation();
  const { state } = useAppContext();

  // Skip all weight monitoring if disabled
  const isWeightMonitoringDisabled = state.settings.disableWeightMonitoring;

  // Determine if we're on home screen or in experience
  const isHomeScreen = location.pathname === '/';
  const inExperience = !isHomeScreen && location.pathname !== '/add-empty';

  // Allow weight changes on Add Empty page (user is expected to add items)
  const allowWeightChanges = location.pathname === '/add-empty';

  // Set baseline weight when entering home screen
  useEffect(() => {
    // Skip if weight monitoring is disabled
    if (isWeightMonitoringDisabled) {
      return;
    }

    const updateBaseline = async () => {
      if (isHomeScreen && !allowWeightChanges) {
        try {
          const response = await ApiService.getWeight();
          if (response.success) {
            setWeightData(prev => ({
              ...prev,
              expectedWeight: response.expected_weight || 0,
            }));
          }
        } catch (error) {
          console.error('Failed to get baseline weight:', error);
        }
      }
    };

    updateBaseline();
  }, [location.pathname, isHomeScreen, allowWeightChanges, isWeightMonitoringDisabled]);

  // Monitor for weight fluctuations
  useEffect(() => {
    // Skip if weight monitoring is disabled
    if (isWeightMonitoringDisabled) {
      return;
    }

    // Don't check on pages where weight is expected to change
    if (allowWeightChanges) {
      setShowWarning(false);
      return;
    }

    const checkFluctuation = async () => {
      try {
        const response = await ApiService.checkFluctuation();

        if (response.success && response.is_fluctuating) {
          setWeightData({
            currentWeight: response.current_weight || 0,
            expectedWeight: response.expected_weight || 0,
            deviation: response.deviation || 0,
            severity: response.severity || 'none',
            inExperience: inExperience,
          });
          setShowWarning(true);
        } else {
          setShowWarning(false);
        }
      } catch (error) {
        console.error('Failed to check fluctuation:', error);
        setShowWarning(false);
      }
    };

    // Check immediately and then every 2 seconds
    checkFluctuation();
    const interval = setInterval(checkFluctuation, 2000);

    return () => clearInterval(interval);
  }, [location.pathname, allowWeightChanges, inExperience, isWeightMonitoringDisabled]);

  // Auto-dismiss warning when weight returns to normal
  useEffect(() => {
    if (showWarning && weightData.severity === 'none') {
      setShowWarning(false);
    }
  }, [showWarning, weightData.severity]);

  // Only render if warning should be shown
  if (!showWarning) {
    return null;
  }

  // HIDDEN FOR DEMO - Don't show weight warnings
  return null;

  // Commented out for demo - original JSX code below
  /*
  const { currentWeight, expectedWeight, deviation, severity, inExperience } = weightData;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9998] pointer-events-none"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.85)' }}
      />

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none"
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          transition={{ duration: 0.3, type: 'spring' }}
          className="bg-white rounded-3xl shadow-2xl p-12 max-w-2xl mx-4 text-center pointer-events-auto"
        >
          <motion.div
            animate={{
              scale: [1, 1.1, 1],
              rotate: [0, -5, 5, -5, 0],
            }}
            transition={{
              duration: 0.8,
              repeat: Infinity,
              repeatDelay: 0.3,
            }}
            className="flex justify-center mb-6"
          >
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
              <svg
                className="w-14 h-14 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2.5}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </motion.div>

          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            {inExperience ? 'Weight Changed' : 'Unexpected Weight Detected'}
          </h2>

          <p className="text-xl text-gray-700 mb-6">
            {inExperience
              ? 'Please keep the scale stable during the process'
              : 'Please remove items from the scale to continue'}
          </p>

          <div className="bg-gray-100 rounded-2xl p-6 mb-6">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-sm text-gray-500 mb-1">Current</div>
                <div className="text-2xl font-bold text-gray-900">
                  {currentWeight.toFixed(0)}g
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Expected</div>
                <div className="text-2xl font-bold text-green-600">
                  {expectedWeight.toFixed(0)}g
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-500 mb-1">Difference</div>
                <div
                  className={`text-2xl font-bold ${
                    severity === 'significant' ? 'text-red-600' : 'text-orange-600'
                  }`}
                >
                  {currentWeight > expectedWeight ? '+' : ''}
                  {(currentWeight - expectedWeight).toFixed(0)}g
                </div>
              </div>
            </div>
          </div>

          <div
            className={`border-2 rounded-2xl p-6 ${
              severity === 'significant'
                ? 'bg-red-50 border-red-200'
                : 'bg-amber-50 border-amber-200'
            }`}
          >
            <p
              className={`text-lg font-semibold mb-2 ${
                severity === 'significant' ? 'text-red-900' : 'text-amber-900'
              }`}
            >
              Please return the scale to expected weight
            </p>
            <p
              className={`text-sm ${
                severity === 'significant' ? 'text-red-700' : 'text-amber-700'
              }`}
            >
              {inExperience
                ? 'Do not add or remove items during the experience'
                : 'Remove any objects from the scale'}
            </p>
          </div>

          <div className="flex justify-center items-center gap-2 mt-8">
            <motion.div
              className="w-3 h-3 rounded-full bg-orange-500"
              animate={{
                opacity: [1, 0.3, 1],
                scale: [1, 1.3, 1],
              }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
              }}
            />
            <span className="text-gray-500 text-sm">
              Waiting for weight to return to {expectedWeight.toFixed(0)}g...
            </span>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
  */
};

export default FluctuationWarning;
